import React from 'react'
import { Link } from 'react-router-dom'
import {
    Drawer,
    Dropdown,
    Grid,
    Pagination,
} from '@softwaredev_cegeka/stix-core'
import { authenticatedUserStore, notifierStore } from 'src/stores'
import { AuthApi } from 'src/api/authApi'
import { GroupTable } from 'src/components/EntityTable'
import GroupDrawerContent from 'src/components/Drawer/GroupDrawerContent'
import DrawerHeader from '../components/Drawer/DrawerHeader'
import { observer } from 'mobx-react'

class GroupRoute extends React.Component {
    state = {
        groups: [],
        selectedGroup: null,
        page: 1,
        totalPages: 1,
        totalElements: 1,
        loading: false,
        params: {},
    }

    authClient = new AuthApi(authenticatedUserStore.authToken)

    componentDidMount() {
        this.fetchAndStoreGroups()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            prevProps.selectedOrganization !== this.props.selectedOrganization
        ) {
            this.fetchAndStoreGroups(1)
        }
    }

    // Cancel still ongoing get requests, to prevent updating state and changing the page to the previous clicked link
    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    fetchAndStoreGroups = (page = this.state.page) => {
        this.setState({ loading: true })
        this.authClient
            .fetchGroups(
                page,
                this.props.history,
                this.state.params,
                authenticatedUserStore.selectedOrganization?.id
            )
            .then(response => {
                this.setState({
                    groups: response?.data?.results,
                    totalPages: response?.data?._page.total_pages,
                    totalElements: response?.data?._page.total_elements,
                    loading: false,
                    page: page,
                })
            })
            .catch(err => console.error(err))
    }

    rowSelectHandler = async (selectObj, setDrawerState) => {
        this.setState(
            {
                selectedGroup: selectObj?.original.id,
            },
            () => setDrawerState(true)
        )
    }

    deleteGroup = (groupId, groupName) => {
        this.authClient
            .deleteGroup(groupId)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreGroups()
                    notifierStore.setToastInfo(
                        `Group ${groupName} successfully deleted`,
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        `Failed to remove the group ${groupName}`,
                        false
                    )
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(
                    err.response?.data?.error ||
                        'Failed to remove group ' + groupName,
                    false
                )
            })
            .finally(notifierStore.setShowMessage(true))
    }

    addUser = userId => {
        this.authClient
            .addUserToGroup(userId, this.state.selectedGroup)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreGroups()
                    notifierStore.setToastInfo('User successfully added', true)
                } else {
                    notifierStore.setToastInfo('Failed to add user', false)
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(notifierStore.setShowMessage(true))
    }

    removeUser = userId => {
        this.authClient
            .removeUserFromGroup(userId, this.state.selectedGroup)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreGroups()
                    notifierStore.setToastInfo(
                        'User successfully removed',
                        true
                    )
                } else {
                    notifierStore.setToastInfo('Failed to remove user', false)
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(notifierStore.setShowMessage(true))
    }

    setParams = params => {
        this.setState(
            {
                params: params,
            },
            () => this.fetchAndStoreGroups(1)
        )
    }

    handlePageChange = page => {
        this.setState({ page: page }, () => this.fetchAndStoreGroups())
    }

    getSelectedGroup() {
        return this.state.groups.find(group => {
            return group.id === parseInt(this.state.selectedGroup)
        })
    }

    render() {
        const selectedGroup = this.getSelectedGroup() || {}
        return (
            <div className="entity-drawer">
                <Drawer
                    type="fixed"
                    panelTitle={
                        <DrawerHeader
                            title={selectedGroup.name}
                            path={`/group-form/${selectedGroup.id}`}
                            isAD={
                                Array.isArray(selectedGroup.mapped_ad_groups) &&
                                selectedGroup.mapped_ad_groups.length
                            }
                            isPublic={selectedGroup.isPublic}
                        />
                    }
                    panelContent={
                        <GroupDrawerContent
                            {...this.props}
                            selectedGroup={selectedGroup}
                            addUser={this.addUser}
                            removeUser={this.removeUser}
                        />
                    }
                >
                    <Grid.Container>
                        <div className={'page-header'}>
                            <h1 className={'content-title'}>Groups overview</h1>
                            {process.env
                                .REACT_APP_ENABLE_EDIT_GROUP_FUNCTIONALITY ===
                                'true' && (
                                <Dropdown.DropdownButton
                                    variant="secondary"
                                    title="Create"
                                >
                                    <Dropdown.Item as={Link} to={'/group-form'}>
                                        Group
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        as={Link}
                                        to={'/default-groups/create'}
                                    >
                                        Default groups
                                    </Dropdown.Item>
                                </Dropdown.DropdownButton>
                            )}
                        </div>

                        <Drawer.Context.Consumer>
                            {({ setDrawerState }) => (
                                <div className="data-table-container">
                                    <GroupTable
                                        data={this.state.groups}
                                        loading={this.state.loading}
                                        params={this.state.params}
                                        setParams={this.setParams}
                                        rowSelectHandler={selectObj =>
                                            this.rowSelectHandler(
                                                selectObj,
                                                setDrawerState
                                            )
                                        }
                                        deleteAction={(groupId, groupName) =>
                                            this.deleteGroup(groupId, groupName)
                                        }
                                        addUserAction={userId =>
                                            this.addUser(userId)
                                        }
                                    />
                                </div>
                            )}
                        </Drawer.Context.Consumer>
                        {this.state.totalPages >= 2 && (
                            <div className="pagination-wrapper">
                                <Pagination
                                    lastPage={this.state.totalPages}
                                    currentPage={this.state.page}
                                    onClick={
                                        this.state.loading
                                            ? () => {}
                                            : this.handlePageChange
                                    }
                                />
                            </div>
                        )}
                    </Grid.Container>
                </Drawer>
            </div>
        )
    }
}

export default observer(GroupRoute)
